import React, { useEffect } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { BookOpen, Database, ExternalLink, FileText, Github } from 'lucide-react';

const Publication = () => {

  useEffect(() => {
    document.title = 'Publication & Database Information'; // Set the page title
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Publication & Database Information
        </h1>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-semibold text-gray-900">Research Publication</h2>
                <p className="mt-2 text-gray-600">
                  SynaptopathyDB is based on research published in the following paper:
                </p>
                <div className="mt-4 bg-blue-50 p-4 rounded-md border border-blue-200">
                  <h3 className="font-medium text-blue-900">Synaptome.db: a bioconductor package for synaptic proteomics data</h3>
                  <p className="mt-2 text-sm text-blue-800">
                    By Oksana Sorokina, Anatoly Sorokin, J Douglas Armstrong. Published in Bioinformatics Advances, November 2022.
                  </p>
                  <div className="mt-3">
                    <a
                      href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9710567/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800"
                    >
                      View Publication <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <Database className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-semibold text-gray-900">Synaptome.db Database</h2>
                <p className="mt-2 text-gray-600">
                  SynaptopathyDB leverages the comprehensive Synaptome.db database, which combines data from 58 mammalian synapse proteome studies published between 2000-2020.
                </p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <h3 className="font-medium text-lg text-gray-900">Database Statistics</h3>
                    <ul className="mt-2 space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-500 mr-2">•</span>
                        <span>Over 8,000 proteins expressed at mammalian synapses</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-500 mr-2">•</span>
                        <span>5,560 post-synaptic proteome gene identifiers</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-500 mr-2">•</span>
                        <span>2,772 presynaptic unique gene IDs</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-500 mr-2">•</span>
                        <span>7,198 whole synaptosome unique gene IDs</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <h3 className="font-medium text-lg text-gray-900">Protein-Protein Interactions</h3>
                    <ul className="mt-2 space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-100 text-purple-500 mr-2">•</span>
                        <span>Post-synaptic PPI network: 4,817 nodes and 27,788 edges</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-100 text-purple-500 mr-2">•</span>
                        <span>Presynaptic PPI network: 2,221 nodes and 8,678 edges</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-100 text-purple-500 mr-2">•</span>
                        <span>PPIs from BioGRID, Intact, and DIP databases</span>
                      </li>
                      <li className="flex items-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-100 text-purple-500 mr-2">•</span>
                        <span>Filtered for direct physical interactions with high confidence</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-semibold text-gray-900">How SynaptopathyDB Uses Synaptome.db</h2>
                <p className="mt-2 text-gray-600">
                  Our application extends the Synaptome.db research database with a user-friendly web interface to make this valuable data accessible to researchers worldwide.
                </p>

                <div className="mt-6 bg-indigo-50 p-5 rounded-lg">
                  <h3 className="font-medium text-indigo-900">Key Implementation Features:</h3>
                  <ul className="mt-3 space-y-3 text-indigo-800">
                    <li className="flex">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-200 text-indigo-700 mr-3 flex-shrink-0">1</span>
                      <span>We've implemented comprehensive search interfaces for genes, diseases, and papers, allowing researchers to quickly find relevant information</span>
                    </li>
                    <li className="flex">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-200 text-indigo-700 mr-3 flex-shrink-0">2</span>
                      <span>The database's SQL structure has been mapped to efficient APIs that power our search functionality while maintaining data integrity</span>
                    </li>
                    <li className="flex">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-200 text-indigo-700 mr-3 flex-shrink-0">3</span>
                      <span>We've enhanced the original dataset with additional linkage to external resources like NCBI Gene, OMIM, and Disease Ontology</span>
                    </li>
                    <li className="flex">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-200 text-indigo-700 mr-3 flex-shrink-0">4</span>
                      <span>Our filtering mechanisms allow researchers to view proteins by confidence level (discovery rate across studies) to separate core synaptic proteins from potential contaminants</span>
                    </li>
                    <li className="flex">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-200 text-indigo-700 mr-3 flex-shrink-0">5</span>
                      <span>All data provenance is maintained, enabling researchers to trace information back to original research papers</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a
                href="https://bioconductor.org/packages/release/data/annotation/html/synaptome.db.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-md hover:bg-blue-50 transition-colors"
              >
                <ExternalLink className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">Bioconductor Package</h3>
                  <p className="text-sm text-gray-500">Access Synaptome.db on Bioconductor</p>
                </div>
              </a>

              <a
                href="https://doi.org/10.7488/ds/3771"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-md hover:bg-blue-50 transition-colors"
              >
                <Database className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">Edinburgh DataShare</h3>
                  <p className="text-sm text-gray-500">Access the SQLite implementation</p>
                </div>
              </a>

              <a
                href="https://doi.org/10.25493/VA01-BRD"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-md hover:bg-blue-50 transition-colors"
              >
                <ExternalLink className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">EBRAINS</h3>
                  <p className="text-sm text-gray-500">Access hosted version on EBRAINS</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Publication;